// src/BlitzHowTo/tutorialStorage.js

const TUTORIAL_STORAGE_PREFIX = 'blitz_tutorial_seen_';
const TUTORIAL_SKIPPED_PREFIX = 'blitz_tutorial_skipped_';

export const tutorialStorage = {
  /**
   * Check if a tutorial has been seen by the user
   */
  hasSeen: (tutorialId, userIdentifier) => {
    if (!tutorialId || !userIdentifier) return false;
    
    const key = `${TUTORIAL_STORAGE_PREFIX}${userIdentifier}`;
    
    try {
      const seenTutorials = JSON.parse(localStorage.getItem(key) || '[]');
      return seenTutorials.includes(tutorialId);
    } catch (error) {
      console.warn('Failed to check tutorial status:', error);
      return false;
    }
  },

  /**
   * Mark a tutorial as seen
   */
  markAsSeen: (tutorialId, userIdentifier) => {
    if (!tutorialId || !userIdentifier) return;
    
    const key = `${TUTORIAL_STORAGE_PREFIX}${userIdentifier}`;
    
    try {
      const seenTutorials = JSON.parse(localStorage.getItem(key) || '[]');
      
      if (!seenTutorials.includes(tutorialId)) {
        seenTutorials.push(tutorialId);
        localStorage.setItem(key, JSON.stringify(seenTutorials));
      }
    } catch (error) {
      console.warn('Failed to mark tutorial as seen:', error);
    }
  },

  /**
   * Check if user has skipped a tutorial
   */
  hasSkipped: (tutorialId, userIdentifier) => {
    if (!tutorialId || !userIdentifier) return false;
    
    const key = `${TUTORIAL_SKIPPED_PREFIX}${userIdentifier}`;
    
    try {
      const skippedTutorials = JSON.parse(localStorage.getItem(key) || '[]');
      return skippedTutorials.includes(tutorialId);
    } catch (error) {
      console.warn('Failed to check skip status:', error);
      return false;
    }
  },

  /**
   * Mark a tutorial as skipped
   */
  markAsSkipped: (tutorialId, userIdentifier) => {
    if (!tutorialId || !userIdentifier) return;
    
    const key = `${TUTORIAL_SKIPPED_PREFIX}${userIdentifier}`;
    
    try {
      const skippedTutorials = JSON.parse(localStorage.getItem(key) || '[]');
      
      if (!skippedTutorials.includes(tutorialId)) {
        skippedTutorials.push(tutorialId);
        localStorage.setItem(key, JSON.stringify(skippedTutorials));
      }
      
      // Also mark as seen so it doesn't show again
      this.markAsSeen(tutorialId, userIdentifier);
    } catch (error) {
      console.warn('Failed to mark tutorial as skipped:', error);
    }
  },

  /**
   * Reset tutorial status (useful for testing or user preference)
   */
  reset: (tutorialId, userIdentifier) => {
    if (!userIdentifier) return;
    
    const seenKey = `${TUTORIAL_STORAGE_PREFIX}${userIdentifier}`;
    const skippedKey = `${TUTORIAL_SKIPPED_PREFIX}${userIdentifier}`;
    
    try {
      if (tutorialId) {
        // Reset specific tutorial
        let seenTutorials = JSON.parse(localStorage.getItem(seenKey) || '[]');
        let skippedTutorials = JSON.parse(localStorage.getItem(skippedKey) || '[]');
        
        seenTutorials = seenTutorials.filter(id => id !== tutorialId);
        skippedTutorials = skippedTutorials.filter(id => id !== tutorialId);
        
        localStorage.setItem(seenKey, JSON.stringify(seenTutorials));
        localStorage.setItem(skippedKey, JSON.stringify(skippedTutorials));
      } else {
        // Reset all tutorials for user
        localStorage.removeItem(seenKey);
        localStorage.removeItem(skippedKey);
      }
    } catch (error) {
      console.warn('Failed to reset tutorial status:', error);
    }
  },

  /**
   * Get all seen tutorials for a user
   */
  getAllSeen: (userIdentifier) => {
    if (!userIdentifier) return [];
    
    const key = `${TUTORIAL_STORAGE_PREFIX}${userIdentifier}`;
    
    try {
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch (error) {
      console.warn('Failed to get seen tutorials:', error);
      return [];
    }
  },
};